import { useServerTimeStore } from '@store/serverTimeStore';

/**
 * 서버 시간 기준으로 현재 시간이 displayStartDate와 displayEndDate 범위 내에 있는지 체크하는 공통 함수
 * @param displayStartDate 전시 시작 날짜
 * @param displayEndDate 전시 종료 날짜
 * @param overrideServerTime 서버 시간을 직접 지정 (adminPreviewDate 등)
 * @returns 전시 기간 내에 있으면 true, 아니면 false
 */
export const isInDisplayTimeRange = (
  displayStartDate?: string,
  displayEndDate?: string,
  overrideServerTime?: string
): boolean => {
  // displayStartDate와 displayEndDate가 모두 있는 경우에만 시간 범위 체크
  if (displayStartDate && displayEndDate) {
    const { serverTime, serverTimeOffset, getAdjustedTime } =
      useServerTimeStore.getState();

    let now: Date;

    // 1순위: overrideServerTime (adminPreviewDate 등)
    if (overrideServerTime) {
      // YYYY-MM-DD 형식인 경우 한국 시간대 11:00:00으로 설정
      if (/^\d{4}-\d{2}-\d{2}$/.test(overrideServerTime)) {
        now = new Date(`${overrideServerTime}T11:00:00+09:00`);
      } else {
        now = new Date(overrideServerTime);
      }
    }
    // 2순위: serverTime이 있으면 사용 (서버/클라이언트 일관성 보장)
    else if (serverTime) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(serverTime)) {
        now = new Date(`${serverTime}T11:00:00+09:00`);
      } else {
        now = new Date(serverTime);
      }
    }
    // 3순위: serverTimeOffset이 있으면 조정된 시간 사용
    else if (serverTimeOffset !== null) {
      now = getAdjustedTime();
    }
    // 4순위: 로컬 시간 사용
    else {
      now = new Date();
    }

    const start = new Date(displayStartDate);
    const end = new Date(displayEndDate);
    return now >= start && now < end;
  }
  // displayStartDate만 있는 경우는 기존 로직 유지
  return !!displayStartDate;
};

/**
 * 프로모션 전시 상태를 확인하는 함수
 * @param displayStartDate 전시 시작 날짜
 * @param displayEndDate 전시 종료 날짜
 * @returns 'active' | 'before' | 'after' | 'unknown'
 */
export const getDisplayStatus = (
  displayStartDate?: string,
  displayEndDate?: string
): 'active' | 'before' | 'after' | 'unknown' => {
  if (!displayStartDate || !displayEndDate) {
    return 'unknown';
  }

  const { serverTime, serverTimeOffset, getAdjustedTime } =
    useServerTimeStore.getState();
  let now: Date;

  // serverTime 우선 사용 (서버/클라이언트 일관성 보장)
  if (serverTime) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(serverTime)) {
      now = new Date(`${serverTime}T11:00:00+09:00`);
    } else {
      now = new Date(serverTime);
    }
  } else if (serverTimeOffset !== null) {
    now = getAdjustedTime();
  } else {
    now = new Date();
  }

  const start = new Date(displayStartDate);
  const end = new Date(displayEndDate);

  if (now < start) {
    return 'before'; // 시작 전
  }
  if (now >= end) {
    return 'after'; // 종료 후
  }
  return 'active'; // 진행 중
};
